import { createContext, useContext, useMemo } from 'react';
import { Lock } from 'lucide-react';
import type { BoardResponse } from '../../types/taActionBoard';

/**
 * Whether the board being viewed may reach candidates during the pilot.
 *
 * Provided once by TAActionBoardView from the board payload, so every control
 * anywhere in the tree can ask without the answer being threaded through six
 * layers of props. A control that can email, invite, book or cancel reads
 * `locked` and disables itself with `message` as the reason.
 *
 * THIS IS COSMETIC. The proxy blocks the same actions with 423 regardless of
 * what the client renders (`lib/pilotMode.js`), exactly as the RBAC guard does —
 * a disabled button is a courtesy to the recruiter, never the control that makes
 * the pilot safe. Missing one here is a UX bug, not a candidate-safety bug.
 */
interface PilotLockValue {
  /** True only when the pilot is armed AND this board is not one of the pilot boards. */
  locked: boolean;
  /** The sentence to show on a disabled control. Null when nothing is locked. */
  message: string | null;
  /** Staggered launch: the feature allowlist is in force (some tabs live, some held). */
  featureGate: boolean;
  /** Is this candidate-facing feature live for everyone? False until the gate lists it. */
  featureLive: (feature: string) => boolean;
}

const UNLOCKED: PilotLockValue = {
  locked: false,
  message: null,
  featureGate: false,
  featureLive: () => false,
};

const PilotLockContext = createContext<PilotLockValue>(UNLOCKED);

export function PilotLockProvider({
  pilot,
  children,
}: {
  pilot: BoardResponse['pilot'];
  children: React.ReactNode;
}) {
  const value = useMemo<PilotLockValue>(() => {
    const live = new Set(pilot?.liveFeatures ?? []);
    const featureLive = (feature: string) => live.has(feature);
    const locked = Boolean(pilot?.enabled) && !pilot?.candidateActionsAllowed;
    return {
      locked,
      message: locked ? (pilot?.message ?? null) : null,
      featureGate: Boolean(pilot?.featureGate),
      featureLive,
    };
  }, [pilot]);

  return <PilotLockContext.Provider value={value}>{children}</PilotLockContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const usePilotLock = () => useContext(PilotLockContext);

/**
 * Says once, at the top of the board, what every disabled control below means.
 *
 * Without it a recruiter meets a row of greyed-out buttons and reads it as the
 * board being broken — which is what a tooltip they have to hunt for does not
 * fix. Renders nothing when the board is unlocked, so it disappears the moment
 * the pilot ends.
 */
export function PilotLockBanner() {
  const { locked, message } = usePilotLock();
  if (!locked) return null;

  return (
    <div className="flex items-start gap-3 rounded-lg border border-solid border-[#F5C77E] bg-[#FFFAEB] p-4 text-sm text-[#7A4A0B]">
      <Lock className="mt-0.5 h-4 w-4 flex-none" aria-hidden="true" />
      <div>
        <p className="m-0 font-semibold">Candidate actions are paused on this board</p>
        <p className="m-0 mt-1 text-[#8B5A16]">
          {message ??
            'Sending emails and scheduling interviews is turned off here during the pilot.'}{' '}
          Everything else — reading your board, moving candidates in Lever, archiving — works as
          usual.
        </p>
      </div>
    </div>
  );
}
